<script setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { ChevronRight } from 'lucide-vue-next'
import ChatAvatar from '@/pages/Project/Chat/ChatAvatar.vue'

const route = useRoute()
const router = useRouter()
const projectStore = useProjectStore()
const userStore = useUserStore()

const projectId = computed(() => route.params.project_id)

watch(
  projectId,
  (id) => {
    if (id) projectStore.fetchMembers(id)
  },
  { immediate: true }
)

// Actual project members only — no org-wide roster padding.
const members = computed(() => {
  const meMember = userStore.member
  const all = [
    ...(meMember ? [meMember] : []),
    ...(projectStore.members || []),
  ]
  const seen = new Set()
  return all.filter((m) => {
    if (!m.member_id || seen.has(m.member_id)) return false
    seen.add(m.member_id)
    return true
  })
})

// The pill always shows 3 circles: real members first, gray placeholders after.
const placeholders = computed(() => Math.max(0, 3 - members.value.length))

function openMembers() {
  router.push({ name: 'ProjectMembers', params: { project_id: projectId.value } })
}
</script>

<template>
  <button
    v-if="members.length"
    type="button"
    class="flex h-8 items-center rounded-full border bg-card pl-1 pr-2 transition-colors hover:border-primary/50"
    title="Project members"
    aria-label="Project members"
    @click="openMembers"
  >
    <div class="flex items-center">
      <ChatAvatar
        v-for="(member, i) in members.slice(0, 3)"
        :key="member.member_id"
        :author="member"
        size="sm"
        class="ring-2 ring-background"
        :class="i > 0 ? '-ml-2' : ''"
      />
      <span
        v-for="i in placeholders"
        :key="`placeholder-${i}`"
        class="size-6 shrink-0 rounded-full bg-muted ring-2 ring-background"
        :class="members.length + i > 1 ? '-ml-2' : ''"
      ></span>
    </div>
    <span class="ml-1.5 text-sm font-semibold">{{ members.length }}</span>
    <ChevronRight class="ml-0.5 size-3.5 text-muted-foreground" />
  </button>
</template>
